import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Deck } from '../../types';
import { SlideRenderer } from '../slides/SlideRenderer';

interface DeckViewerProps {
  deck: Deck;
  onSlideChange?: (slideIndex: number) => void;
  showControls?: boolean;
}

export const DeckViewer: React.FC<DeckViewerProps> = ({ 
  deck, 
  onSlideChange,
  showControls = true 
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const currentSlide = deck.slides[currentSlideIndex];
  const totalSlides = deck.slides.length;

  const goToSlide = useCallback((index: number) => {
    if (index >= 0 && index < totalSlides) {
      setCurrentSlideIndex(index);
      onSlideChange?.(index);
    }
  }, [totalSlides, onSlideChange]);

  const nextSlide = useCallback(() => {
    goToSlide(currentSlideIndex + 1);
  }, [currentSlideIndex, goToSlide]);

  const previousSlide = useCallback(() => {
    goToSlide(currentSlideIndex - 1);
  }, [currentSlideIndex, goToSlide]);

  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'Space':
          nextSlide();
          break;
        case 'ArrowLeft':
          previousSlide();
          break;
        case 'F11':
        case 'f':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'Home':
          goToSlide(0);
          break;
        case 'End':
          goToSlide(totalSlides - 1);
          break;
        default:
          // Number keys for direct navigation
          const slideNumber = parseInt(e.key);
          if (slideNumber >= 1 && slideNumber <= totalSlides) {
            goToSlide(slideNumber - 1);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [nextSlide, previousSlide, toggleFullscreen, goToSlide, totalSlides]);

  return (
    <div className="relative w-full h-screen bg-off-white">
      {/* Main slide area */}
      <div className="w-full h-full">
        {currentSlide && (
          <motion.div
            key={currentSlideIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="w-full h-full"
          >
            <SlideRenderer slide={currentSlide} isActive={true} />
          </motion.div>
        )}
      </div>

      {/* Slide controls */}
      {showControls && (
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <motion.div
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-soft px-6 py-3 flex items-center space-x-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            {/* Previous button */}
            <button
              onClick={previousSlide}
              disabled={currentSlideIndex === 0}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Slide counter */}
            <span className="text-sm font-medium text-soft-black min-w-16 text-center">
              {currentSlideIndex + 1} / {totalSlides}
            </span>

            {/* Next button */}
            <button
              onClick={nextSlide}
              disabled={currentSlideIndex === totalSlides - 1}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Fullscreen toggle */}
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors ml-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4m-4 0l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
              </svg>
            </button>
          </motion.div>
        </div>
      )}

      {/* Slide thumbnails/overview (hidden by default, can be toggled) */}
      <div className="absolute top-4 right-4 opacity-30 hover:opacity-100 transition-opacity duration-200">
        <div className="text-xs text-warm-gray bg-white/80 rounded-lg px-3 py-2">
          {deck.name} • {deck.stakeholder}
        </div>
      </div>
    </div>
  );
};